import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, first, map, mergeMap, skipUntil, switchMap, throttleTime, withLatestFrom } from 'rxjs/operators';
import { zip } from 'rxjs';
import { getActualPresence, getActualPresenceSuccess, initializeModule, loadSystemPresences, systemPresencesLoaded, togglePresence, updatePresence, updatePresenceSuccess } from '../actions/presence.actions';
import { actualUserLoaded } from '../../users/actions/user.actions';
import { selectUser } from '../../users/selectors/user.selectors';
import { selectActivePresence, selectActivePresenceIsOnQueue, selectAwayPresence, selectOnQueuePresence, selectPresenceNotificationTopicName } from '../selectors/presence.selectors';
import { PresenceApiService } from '../services/presence-api.service';
import { connectToNotificationChannelSuccess, notificationArrived, notificationChannelCreated, subscribeToNotifications } from '../../notifications/actions/notification.actions';
import { PresenceNotification } from '../models/presence-notification.model';
import { presenceTopicName } from '../helpers/notification-topics.helper';

@Injectable()
export class GenesysCloudPresenceEffects {
  static SetPresenceThrottleTimeMillis = 200;

  readonly init$ = createEffect(() => this.actions$.pipe(
    ofType(initializeModule),
    map(() => loadSystemPresences())
  ));

  readonly loadSystemPresences$ = createEffect(() => this.actions$.pipe(
    ofType(loadSystemPresences),
    mergeMap(() => this.presenceApi.getSystemPresences$()),
    map(systemPresences => systemPresencesLoaded({ systemPresences }))
  ));

  readonly initialActivePresence$ = createEffect(() => zip(
    this.actions$.pipe(ofType(actualUserLoaded)),
    this.actions$.pipe(ofType(systemPresencesLoaded))
  ).pipe(
    first(),
    map(([{ user }]) => updatePresenceSuccess({ systemPresenceId: user.presence.presenceDefinition.id }))
  ));

  readonly updatePresence$ = createEffect(() => this.actions$.pipe(
    ofType(updatePresence),
    throttleTime(GenesysCloudPresenceEffects.SetPresenceThrottleTimeMillis),
    withLatestFrom(this.store.select(selectUser), this.store.select(selectActivePresence)),
    filter(([{ systemPresenceId }, _, activePresence]) => systemPresenceId !== activePresence?.id),
    // switchMap is intended to cancel previous presence update requests
    switchMap(([{ systemPresenceId }, user]) => this.presenceApi.setUserPresence$(user.id, systemPresenceId))
  ), { dispatch: false });

  readonly subscribeToPresenceNotifications$ = createEffect(() => zip(
    this.actions$.pipe(ofType(actualUserLoaded)),
    this.actions$.pipe(ofType(notificationChannelCreated))
  ).pipe(
    first(),
    map(([{ user }, { channel }]) => subscribeToNotifications({
      userId: user.id,
      channelId: channel.id,
      topics: [presenceTopicName(user.id)]
    }))
  ));

  readonly handlePresenceNotification$ = createEffect(() => this.actions$.pipe(
    ofType(notificationArrived),
    concatLatestFrom(() => this.store.select(selectPresenceNotificationTopicName)),
    filter(([{ notification }, topicName]) => notification.topicName === topicName),
    map(([{ notification }, _]) => <PresenceNotification>notification.eventBody),
    map(presenceNotification => updatePresenceSuccess({ systemPresenceId: presenceNotification.presenceDefinition.id }))
  ));

  readonly togglePresence$ = createEffect(() => this.actions$.pipe(
    ofType(togglePresence),
    withLatestFrom(
      this.store.select(selectActivePresenceIsOnQueue),
      this.store.select(selectOnQueuePresence),
      this.store.select(selectAwayPresence)
    ),
    map(([_, isOnQueue, onQueuePresence, awayPresence]) => isOnQueue ? awayPresence.id : onQueuePresence.id),
    map(systemPresenceId => updatePresence({ systemPresenceId }))
  ));

  private readonly initialPresenceLoaded$ = this.actions$.pipe(
    ofType(updatePresenceSuccess)
  );

  readonly syncActualPresenceOnReconnect$ = createEffect(() => this.actions$.pipe(
    skipUntil(this.initialPresenceLoaded$),
    ofType(connectToNotificationChannelSuccess),
    filter(({ isReconnect }) => isReconnect),
    map(() => getActualPresence())
  ));

  readonly getActualPresence$ = createEffect(() => this.actions$.pipe(
    ofType(getActualPresence),
    concatLatestFrom(() => this.store.select(selectUser)),
    switchMap(([_, user]) => this.presenceApi.getUserPresence$(user.id)),
    map(userPresence => getActualPresenceSuccess({ systemPresenceId: userPresence.presenceDefinition.id }))
  ));

  constructor(private readonly actions$: Actions,
              private readonly presenceApi: PresenceApiService,
              private readonly store: Store
  ) {
  }
}
